import type { LineItemFragmentFragment } from '@packages/gql/generated/shopping/graphql';
import { useNotification } from '@packages/shared/src/providers/NotificationProvider/useNotification';
import { CircularProgress } from '@packages/shared/src/ui/CircularProgress/CircularProgress';
import { Button } from '@packages/shared/src/ui/Button/Button';
import { getFragmentData } from '@packages/gql/generated/shopping';
import { defineMessages, useIntl } from 'react-intl';
import { getBridge } from '@packages/shared/src/utils/getBridge';
import { useAuthMutation } from '@packages/shared/src/hooks/useAuthMutation/useAuthMutation';
import type { GlycerinCartProduct } from '@packages/tracking/src/types/types';
import type {
  GTMEventGlycerinRemoveFromCart,
  GTMEventGlycerinAddToWishList,
} from '@packages/tracking/src/types/events';
import { useTracking } from '@packages/tracking/src/hooks/useTracking/useTracking';
import { dispatchButtonTracking } from '@packages/shared/src/ui/Button/dispatchButtonTracking';
import { RemoveFromBasketDocument } from '@packages/gql/generated/shopping/RemoveFromBasketDocument';
import { AddToWishlistDocument } from '@packages/gql/generated/shopping/AddToWishlistDocument';
import { BasketFragmentFragmentDoc } from '@packages/gql/generated/shopping/BasketFragmentFragmentDoc';
import type { ButtonHTMLAttributes } from 'react';
import { buttonStylingTailwind, iconStylingTailwind } from './Actions.styles';

export interface AddToWishlistActionProps {
  lineItemId: LineItemFragmentFragment['id'];
  sku: LineItemFragmentFragment['product']['sku'];
  glycerinProduct: GlycerinCartProduct;
  bridge?: any; // TODO replace this with stronger type inference
}
const messages = defineMessages({
  buttonText: {
    id: 'AddToWishlistAction.ButtonText',
    defaultMessage: 'Merken',
    description: 'this will be displayed on the button',
  },
  successMessage: {
    id: 'AddToWishlistAction.SuccessMessage',
    defaultMessage: 'Dein Artikel wurde erfolgreich auf den Merkzettel verschoben.',
    description: 'this will be displayed on notification provider',
  },
});

export const AddToWishlistAction: React.FC<
  AddToWishlistActionProps & Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'color'>
> = ({ lineItemId, sku, glycerinProduct, onClick, ...props }) => {
  const { formatMessage, locale } = useIntl();
  const dispatchGlycerin = useTracking();
  const [basketState, executeBasketMutation] = useAuthMutation(RemoveFromBasketDocument);
  const [wishlistState, executeWishlistMutation] = useAuthMutation(AddToWishlistDocument);
  const { addNotification } = useNotification();

  const onAddWishlistHandler = (event: React.MouseEvent<HTMLButtonElement>) => {
    dispatchButtonTracking({
      eventDispatcher: dispatchGlycerin,
      type: 'button',
      clickTrackingProps: {
        category: 'Artikel Merken',
        label: formatMessage(messages.buttonText),
      },
    });
    if (!basketState.fetching && lineItemId && !wishlistState.fetching && sku) {
      onClick?.(event);
      // we still need implementation for bridge
      // istanbul ignore next
      getBridge().updateWishlist();
      // istanbul ignore next
      getBridge().updateBasket();
      // We need to wait for both mutations to finish before we can decide if it was successful or not
      Promise.allSettled([
        executeWishlistMutation({ locale, sku, unspecified: false }),
        executeBasketMutation({ lineItemId, locale }),
      ]).then(([wishlistResult, basketResult]) => {
        if (wishlistResult.status === 'fulfilled' && basketResult.status === 'fulfilled') {
          const basket = getFragmentData(
            BasketFragmentFragmentDoc,
            basketResult.value.data?.removeFromBasket,
          );

          if (basket && basket.success) {
            addNotification({
              severity: 'success',
              message: formatMessage(messages.successMessage),
              duration: 5000,
              onClose: () => {},
            });
            dispatchGlycerin<GTMEventGlycerinRemoveFromCart>({
              event: 'RemoveFromCart',
              RemoveFromCartData: {
                removedProducts: [glycerinProduct],
              },
            });
            dispatchGlycerin<GTMEventGlycerinAddToWishList>({
              event: 'AddToWishList',
              AddToWishListData: {
                addedProducts: [glycerinProduct],
              },
            });
          }
        }
      });
    }
  };
  return (
    <Button
      className={buttonStylingTailwind}
      onClick={onAddWishlistHandler}
      data-testid="wishlist-action"
      variant="icon-flat"
      size="base"
      {...props}
    >
      {basketState.fetching && wishlistState.fetching ? (
        <CircularProgress className={iconStylingTailwind} />
      ) : (
        <div className="icon-emp-wishlist size-4 md:size-3" />
      )}
      {formatMessage(messages.buttonText)}
    </Button>
  );
};
